import { normalizeAgentId } from "@openclaw/normalization-core/agent-id";
import { normalizeSessionKeyForComparison } from "@openclaw/normalization-core/session-key-comparison";
import {
  normalizeLowercaseStringOrEmpty,
  normalizeOptionalLowercaseString,
  normalizeOptionalString,
} from "@openclaw/normalization-core/string-coerce";
import {
  buildAgentMainSessionKey,
  DEFAULT_MAIN_KEY,
  normalizeMainKey,
  parseAgentSessionKeyParts,
  type ParsedAgentSessionKey,
} from "@openclaw/session-url-contract";
import type { GatewayHelloOk } from "../../api/gateway.ts";

export { buildAgentMainSessionKey, DEFAULT_MAIN_KEY };
export const DEFAULT_AGENT_ID = "main";
const DEFAULT_MAIN_SESSION_KEY = buildAgentMainSessionKey({
  agentId: DEFAULT_AGENT_ID,
  mainKey: DEFAULT_MAIN_KEY,
});

// Normalization depends only on the input string. Bound retention across roster churn;
// clearing at 4,096 entries needs no clock or session/config invalidation.
const comparisonKeys = new Map<string, string>();
const COMPARISON_KEY_CACHE_LIMIT = 4_096;

export type UiSessionDefaultsHost = {
  assistantAgentId?: string | null;
  agentsList?: { defaultId?: string | null; mainKey?: string | null; scope?: string | null } | null;
  hello?: Pick<GatewayHelloOk, "snapshot"> | null;
};

type UiSessionDefaults = NonNullable<NonNullable<GatewayHelloOk["snapshot"]>["sessionDefaults"]>;

export { normalizeAgentId };

export function resolveUiSessionRowAgentId(
  row: { key: string; agentId?: string },
  fallbackAgentId: string,
): string {
  return parseAgentSessionKey(row.key)?.agentId ?? row.agentId ?? fallbackAgentId;
}

export function parseAgentSessionKey(
  sessionKey: string | undefined | null,
): ParsedAgentSessionKey | null {
  // Display ownership historically tolerates empty segments and folds the tail.
  // Store identities and URL literals apply their own stricter policies.
  const normalized = normalizeLowercaseStringOrEmpty(sessionKey);
  return parseAgentSessionKeyParts(
    normalized.startsWith(":") || normalized.endsWith(":") || normalized.includes("::")
      ? normalized.split(":").filter(Boolean).join(":")
      : normalized,
  );
}

export function parseSessionKeyParts(
  key: string,
): { agentId: string; channel: string; accountId: string } | null {
  const match = /^agent:([^:]+):([^:]+):(.+)$/.exec(key);
  return match
    ? { agentId: match[1] as string, channel: match[2] as string, accountId: match[3] as string }
    : null;
}

export function resolveUiSessionNavigationParentKey(
  row: { parentSessionKey?: string | null; spawnedBy?: string | null } | null | undefined,
): string | undefined {
  return normalizeOptionalString(row?.parentSessionKey) ?? normalizeOptionalString(row?.spawnedBy);
}

// Mirrors the Gateway policy in src/config/sessions/session-pin-policy.ts.
// Durable dashboard sessions auto-parent to the agent main root for flow-up
// notices and sidebar threads; that lineage does not make them nested children.
export function isPinnableUiSessionRow(row: {
  key: string;
  parentSessionKey?: string | null;
  spawnedBy?: string | null;
  sidebarRoot?: boolean;
}): boolean {
  if (isSubagentSessionKey(row.key)) {
    return false;
  }
  if (row.sidebarRoot) {
    return true;
  }
  if (normalizeOptionalString(row.spawnedBy)) {
    return false;
  }
  const parentSessionKey = normalizeOptionalString(row.parentSessionKey);
  if (!parentSessionKey) {
    return true;
  }
  const parsed = parseAgentSessionKey(row.key);
  if (!parsed?.agentId) {
    return false;
  }
  return parentSessionKey === buildAgentMainSessionKey({ agentId: parsed.agentId });
}

export function normalizeSessionKeyForUiComparison(sessionKey: string | undefined | null): string {
  if (sessionKey == null) {
    return "";
  }
  const cached = comparisonKeys.get(sessionKey);
  if (cached !== undefined) {
    return cached;
  }
  const normalized = normalizeSessionKeyForComparison(sessionKey);
  if (comparisonKeys.size >= COMPARISON_KEY_CACHE_LIMIT) {
    comparisonKeys.clear();
  }
  comparisonKeys.set(sessionKey, normalized);
  return normalized;
}

export function readSessionDefaults(
  host: Pick<UiSessionDefaultsHost, "hello">,
): UiSessionDefaults | undefined {
  return host.hello?.snapshot?.sessionDefaults;
}

export function resolveUiConfiguredMainKey(
  host: Pick<UiSessionDefaultsHost, "agentsList" | "hello">,
): string {
  return normalizeMainKey(host.agentsList?.mainKey ?? readSessionDefaults(host)?.mainKey);
}

export function resolveUiDefaultAgentId(
  host: Pick<UiSessionDefaultsHost, "agentsList" | "hello">,
): string {
  return normalizeAgentId(
    host.agentsList?.defaultId ?? readSessionDefaults(host)?.defaultAgentId ?? DEFAULT_AGENT_ID,
  );
}

export function resolveUiKnownSelectedGlobalAgentId(
  host: Pick<UiSessionDefaultsHost, "assistantAgentId" | "agentsList" | "hello">,
): string | undefined {
  const selectedAgentId =
    host.assistantAgentId ??
    host.agentsList?.defaultId ??
    readSessionDefaults(host)?.defaultAgentId;
  return selectedAgentId ? normalizeAgentId(selectedAgentId) : undefined;
}

export function resolveUiSelectedGlobalAgentId(
  host: Pick<UiSessionDefaultsHost, "assistantAgentId" | "agentsList" | "hello">,
): string {
  return resolveUiKnownSelectedGlobalAgentId(host) ?? DEFAULT_AGENT_ID;
}

export function resolveUiGlobalAliasAgentId(
  host: Pick<UiSessionDefaultsHost, "agentsList" | "hello">,
  sessionKey: string | undefined | null,
): string | null {
  const raw = normalizeOptionalString(sessionKey);
  if (!raw || isUiGlobalSessionKey(raw)) {
    return null;
  }
  const identity = resolveUiConversationIdentity(host, raw);
  return identity.sessionKey === "global" ? (identity.agentId ?? null) : null;
}

export function isUiGlobalSessionKey(sessionKey: string | undefined | null): boolean {
  return normalizeLowercaseStringOrEmpty(sessionKey) === "global";
}

/** True when the configured main session routes to the global stream (session.scope="global"). */
export function isUiGlobalScopeConfigured(
  host: Pick<UiSessionDefaultsHost, "agentsList" | "hello">,
): boolean {
  const scope = normalizeOptionalLowercaseString(host.agentsList?.scope);
  if (scope) {
    return scope === "global";
  }
  return isUiGlobalSessionKey(readSessionDefaults(host)?.mainSessionKey);
}

/** Admission identity shared by browser outboxes and visible conversation matching. */
export function resolveUiConversationIdentity(
  host: UiSessionDefaultsHost,
  sessionKey: string,
  agentIdOverride?: string | null,
): { sessionKey: string; agentId?: string } {
  const raw = sessionKey.trim();
  const parsed = parseAgentSessionKey(raw);
  const knownDefaults = hasUiSessionDefaults(host);
  const mainKey = resolveUiConfiguredMainKey(host);
  const mainCandidate = parsed?.rest ?? raw.toLowerCase();
  const isMain = mainCandidate === DEFAULT_MAIN_KEY || (knownDefaults && mainCandidate === mainKey);
  if (isUiGlobalSessionKey(raw)) {
    const agentId = agentIdOverride?.trim()
      ? normalizeAgentId(agentIdOverride)
      : resolveUiKnownSelectedGlobalAgentId(host);
    return { sessionKey: "global", ...(agentId ? { agentId } : {}) };
  }
  if (!parsed && (!isMain || !knownDefaults)) {
    return { sessionKey: raw };
  }
  const agentId = normalizeAgentId(
    parsed?.agentId ?? normalizeOptionalString(agentIdOverride) ?? resolveUiDefaultAgentId(host),
  );
  let canonicalKey = normalizeSessionKeyForUiComparison(raw);
  if (isMain && knownDefaults) {
    const defaults = readSessionDefaults(host);
    const advertised = normalizeOptionalString(defaults?.mainSessionKey);
    // Hello's explicit target owns default-agent aliases only while its routing
    // facts still agree with the current roster. Never redirect another agent.
    const advertisedApplies =
      advertised &&
      agentId === resolveUiDefaultAgentId(host) &&
      agentId === normalizeAgentId(defaults?.defaultAgentId) &&
      agentId === parseAgentSessionKey(advertised)?.agentId &&
      mainKey === normalizeMainKey(defaults?.mainKey);
    canonicalKey = isUiGlobalScopeConfigured(host)
      ? "global"
      : advertisedApplies
        ? normalizeSessionKeyForUiComparison(advertised)
        : buildAgentMainSessionKey({ agentId, mainKey });
  }
  return { sessionKey: canonicalKey, agentId };
}

export function hasUiSessionDefaults(host: UiSessionDefaultsHost): boolean {
  return host.agentsList != null || readSessionDefaults(host) !== undefined;
}

/** Artifact snapshots and events retain global in their owner-qualified wire key. */
export function scopedSessionArtifactKey(sessionKey: string, agentId?: string): string {
  const key = sessionKey.trim();
  if (!key || parseAgentSessionKey(key) || !agentId?.trim()) {
    return key;
  }
  return `agent:${normalizeAgentId(agentId)}:${key}`;
}

export function canonicalUiSessionKeyForPersistence(
  host: Pick<UiSessionDefaultsHost, "agentsList" | "hello">,
  sessionKey: string | undefined | null,
): string {
  const raw = normalizeOptionalString(sessionKey);
  return raw ? resolveUiConversationIdentity(host, raw).sessionKey : "";
}

function createUiConversationMatcher(
  host: UiSessionDefaultsHost,
  selectedKey: string | undefined | null,
  selectedAgentId?: string | null,
  scopeGlobalRows = false,
) {
  const selected = normalizeOptionalString(selectedKey);
  const explicitSelectedAgent = normalizeOptionalString(selectedAgentId);
  const current = selected
    ? resolveUiConversationIdentity(host, selected, explicitSelectedAgent)
    : null;
  const defaultAgent = resolveUiDefaultAgentId(host);
  const currentAgent = current?.agentId ?? defaultAgent;
  return (candidateKey: string | undefined | null, candidateAgentId?: string | null): boolean => {
    const candidate = normalizeOptionalString(candidateKey);
    if (
      !current ||
      !candidate ||
      (explicitSelectedAgent && normalizeAgentId(explicitSelectedAgent) !== currentAgent)
    ) {
      return false;
    }
    const explicitAgent = normalizeOptionalString(
      candidateAgentId ??
        (scopeGlobalRows && isUiGlobalSessionKey(candidate) ? current.agentId : undefined),
    );
    const other = resolveUiConversationIdentity(host, candidate, explicitAgent ?? defaultAgent);
    const otherAgent = other.agentId ?? defaultAgent;
    return (
      (!explicitAgent || normalizeAgentId(explicitAgent) === otherAgent) &&
      current.sessionKey === other.sessionKey &&
      currentAgent === otherAgent
    );
  };
}

/** Compare selected conversation ownership, rejecting contradictory captured agent facts. */
export function uiConversationMatches(
  host: UiSessionDefaultsHost,
  selectedKey: string | undefined | null,
  candidateKey: string | undefined | null,
  candidateAgentId?: string | null,
  selectedAgentId?: string | null,
): boolean {
  return createUiConversationMatcher(
    host,
    selectedKey,
    selectedAgentId,
  )(candidateKey, candidateAgentId);
}

/** Resolve the selected identity once when matching an entire list. */
export function createUiSessionRowMatcher(
  host: UiSessionDefaultsHost,
  selectedSessionKey: string | undefined | null,
) {
  return createUiConversationMatcher(host, selectedSessionKey, undefined, true);
}

export function uiSessionEventMatches(
  host: UiSessionDefaultsHost & { sessionKey: string },
  eventSessionKey: string | undefined | null,
  eventAgentId?: string | null,
): boolean {
  // Some broadcasts intentionally omit a target; their caller owns run/session fencing.
  return (
    !normalizeOptionalString(eventSessionKey) ||
    uiConversationMatches(host, host.sessionKey, eventSessionKey, eventAgentId)
  );
}

export function isUiSelectedGlobalSessionKey(
  host: Pick<UiSessionDefaultsHost, "agentsList" | "hello">,
  sessionKey: string | undefined | null,
): boolean {
  return canonicalUiSessionKeyForPersistence(host, sessionKey) === "global";
}

export function resolveUiSelectedSessionAgentId(
  host: Pick<UiSessionDefaultsHost, "assistantAgentId" | "agentsList" | "hello"> & {
    sessionKey?: string | null;
  },
  sessionKey: string | undefined | null = host.sessionKey,
): string | undefined {
  return resolveUiConversationIdentity(host, sessionKey ?? "").agentId;
}

export function uiSessionRowMatchesSelectedChat(
  host: UiSessionDefaultsHost,
  rowKey: string | undefined | null,
  selectedSessionKey: string | undefined | null,
  rowAgentId?: string | null,
): boolean {
  return createUiSessionRowMatcher(host, selectedSessionKey)(rowKey, rowAgentId);
}

export function normalizeDefaultMainSessionAliasForUi(
  sessionKey: string | undefined | null,
): string {
  const normalized = normalizeSessionKeyForUiComparison(sessionKey);
  return normalized === DEFAULT_MAIN_KEY ? DEFAULT_MAIN_SESSION_KEY : normalized;
}

export function areUiSessionKeysEquivalent(
  left: string | undefined | null,
  right: string | undefined | null,
): boolean {
  if (left === right) {
    return Boolean(left?.trim());
  }
  const normalizedLeft = normalizeDefaultMainSessionAliasForUi(left);
  const normalizedRight = normalizeDefaultMainSessionAliasForUi(right);
  return Boolean(normalizedLeft && normalizedRight && normalizedLeft === normalizedRight);
}

export function resolveAgentIdFromSessionKey(sessionKey: string | undefined | null): string {
  const parsed = parseAgentSessionKey(sessionKey);
  return normalizeAgentId(parsed?.agentId ?? DEFAULT_AGENT_ID);
}

function isProtectedSessionLifecycleKey(
  row: { key: string; kind?: string },
  configuredMainKey: string,
): boolean {
  const normalizedKey = normalizeLowercaseStringOrEmpty(row.key);
  if (
    row.kind === "global" ||
    row.kind === "unknown" ||
    normalizedKey === "global" ||
    normalizedKey === "unknown"
  ) {
    return true;
  }
  return (
    row.key === "main" ||
    normalizeLowercaseStringOrEmpty(parseAgentSessionKey(row.key)?.rest) ===
      normalizeMainKey(configuredMainKey)
  );
}

// Archive policy shared by the chat picker, sidebar recents, and Sessions
// table: Gateway drains live work; main/global/unknown rows stay protected.
export function canArchiveSessionRow(
  row: { key: string; kind?: string; sessionId?: string },
  configuredMainKey: string,
): boolean {
  return Boolean(row.sessionId?.trim() && !isProtectedSessionLifecycleKey(row, configuredMainKey));
}

/** Preserve Delete's prior all-idle-or-all-archived batch policy independently of Archive. */
export function canDeleteSessionRows(
  rows: ReadonlyArray<{
    key: string;
    kind?: string;
    hasActiveRun?: boolean;
    archived?: boolean;
  }>,
  configuredMainKey: string,
): boolean {
  return (
    rows.every((row) => row.archived === true) ||
    rows.every(
      (row) => row.hasActiveRun !== true && !isProtectedSessionLifecycleKey(row, configuredMainKey),
    )
  );
}

/** Native catalog adoptions retain their provider transcript outside OpenClaw's delete lifecycle. */
export function isNativeCatalogAdoptionSessionKey(key: string): boolean {
  const rest = parseAgentSessionKey(key)?.rest ?? key;
  return (
    rest.startsWith("plugin:anthropic:catalog-adopt:claude:") ||
    rest.startsWith("harness:codex:supervision:") ||
    rest.startsWith("harness:codex:node-session:")
  );
}

export function isSessionKeyTiedToAgent(
  sessionKey: string | undefined | null,
  agentId: string,
  defaultAgentId: string = DEFAULT_AGENT_ID,
): boolean {
  const normalizedAgentId = normalizeAgentId(agentId);
  const parsed = parseAgentSessionKey(sessionKey);
  if (parsed) {
    return normalizeAgentId(parsed.agentId) === normalizedAgentId;
  }
  return normalizedAgentId === normalizeAgentId(defaultAgentId);
}

function hasSessionKeyPrefix(sessionKey: string | undefined | null, prefix: string): boolean {
  const raw = normalizeLowercaseStringOrEmpty(sessionKey);
  return (
    raw.startsWith(prefix) ||
    normalizeLowercaseStringOrEmpty(parseAgentSessionKey(raw)?.rest).startsWith(prefix)
  );
}

export function isSubagentSessionKey(sessionKey: string | undefined | null): boolean {
  return hasSessionKeyPrefix(sessionKey, "subagent:");
}

/** ACP-backed sessions (`agent:<id>:acp:<uuid>`) belong to the Coding zone, not chat threads. */
export function isAcpSessionKey(sessionKey: string | undefined | null): boolean {
  return hasSessionKeyPrefix(sessionKey, "acp:");
}

/**
 * Dashboard sessions (`agent:<id>:dashboard:<uuid>`) are opened in their own
 * right. A child with such a key was launched with `visible`; every other child
 * of a session runs hidden, as its subagent.
 */
export function isDashboardSessionKey(sessionKey: string | undefined | null): boolean {
  return hasSessionKeyPrefix(sessionKey, "dashboard:");
}
